import Link from "@/src/common/components/Link"
import classNames from "classnames"
import React, { ReactNode } from "react"

import Menu from "../Menu"
import AnimatedArrow from "../icon/AnimatedArrow"
import { useDropdown } from "./hooks"

const NAV_ITEM_CLASS =
  "inline-flex items-center px-4 py-2 justify-center text-base w-fit hover:bg-gray-50 text-stears_black font-jakarta whitespace-nowrap"

const DROPDOWN_TYPES = {
  "rounded-gray": `${NAV_ITEM_CLASS} shadow-sm bg-gray-100 rounded-full dark:rounded-none dark:bg-gray-600 dark:text-stears_white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500 dark:focus:ring-0 dark:focus:ring-offset-0 hover:bg-gray-50 `,
  simple: `${NAV_ITEM_CLASS}  focus:outline-none focus:ring-2 focus:ring-offset-gray-100 focus-visible:ring-indigo-500 hover:bg-gray-50 `
}

export type NavItemDropdownProps = {
  children: ReactNode | ReactNode[]
  label: string
  type?: keyof typeof DROPDOWN_TYPES
  icon?: ReactNode
  menuAlign?: "left" | "center" | "right"
  columns?: "one" | "three"
}

const Dropdown = (props: NavItemDropdownProps) => {
  const {
    children,
    label,
    type = "rounded-gray",
    icon,
    menuAlign = "left",
    columns
  } = props
  const { showMenu, handleShowMenu, parentElementRef, keyboardHandler } =
    useDropdown()

  const btnClass = DROPDOWN_TYPES[type]

  return (
    <div className="inline-block text-left" ref={parentElementRef}>
      <div>
        <button
          id="dropdown"
          type="button"
          className={classNames(
            btnClass,
            "dark:px-5 dark:py-3 dark:text-xs dark:gap-2"
          )}
          onClick={handleShowMenu(!showMenu)}
          aria-expanded={showMenu}
          aria-haspopup={true}
          aria-hidden={!showMenu}
          aria-label={label}
        >
          {icon && (
            <span
              className={classNames(
                "flex justify-center",
                "dark:m-0 dark:gap-2",
                "-m-2 mr-2"
              )}
            >
              {icon}
            </span>
          )}

          {label}

          <div className={"relative bottom-[2px] -ml-1"}>
            <AnimatedArrow
              aria-hidden={!showMenu}
              position={showMenu ? "up" : "down"}
            />
          </div>
        </button>
      </div>
      <div onKeyDown={keyboardHandler}>
        <Menu
          columns={columns}
          visible={showMenu}
          position={`bottom-${menuAlign}`}
        >
          {children}
        </Menu>
      </div>
    </div>
  )
}

export type NavItemLinkProps = {
  children: ReactNode
  href: string
  onClick?: () => void
}

const NavItemLink = (props: NavItemLinkProps) => {
  const { href, children, onClick } = props
  return (
    <Link legacyBehavior href={href}>
      <a onClick={onClick} className={NAV_ITEM_CLASS}>
        {children}
      </a>
    </Link>
  )
}

Dropdown.displayName = "NavItem.Dropdown"
NavItemLink.displayName = "NavItem.Link"

const NavItem = {
  Dropdown,
  Link: NavItemLink
}

export default NavItem
